import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Mirrors the column-allocation algorithm in snippets/mega-menu-list.liquid's
 * 'featured_products' branch (min/max columns reserved for content, desktop
 * vs tablet), so our cards get the identical auto-resize grid behavior.
 * @param {number} cardCount
 * @param {number} listColumnCount - columns occupied by the links list itself
 */
function computeCardColumns(cardCount, listColumnCount) {
  const TOTAL_DESKTOP = 6;
  const TOTAL_TABLET = 4;
  const MIN_CARDS = 2;
  const MAX_CARDS = 3;
  const MIN_CARDS_TABLET = 1;
  const MAX_CARDS_TABLET = 3;

  let desktop =
    listColumnCount + MIN_CARDS > TOTAL_DESKTOP
      ? 2
      : Math.min(TOTAL_DESKTOP - listColumnCount, MAX_CARDS);
  desktop = Math.max(Math.min(desktop, cardCount), 1);

  let tablet =
    listColumnCount + MIN_CARDS_TABLET > TOTAL_TABLET
      ? 1
      : Math.min(TOTAL_TABLET - listColumnCount, MAX_CARDS_TABLET);
  tablet = Math.max(Math.min(tablet, desktop, cardCount), 1);

  return { desktop, tablet };
}

/**
 * Moves hidden "mega-menu-photo-group" blocks into the dropdown of the
 * top-level menu item whose title matches their configured menu_link_label.
 * Cards are wrapped in the same .mega-menu__content-list--products markup
 * used by the built-in "featured products" mega-menu style, so they share
 * its exact grid placement and responsive auto-resize behavior, and any
 * auto-generated product/collection content for that link is hidden so the
 * two don't appear together.
 */
function relocateMegaMenuPhotoCards() {
  const groups = document.querySelectorAll('.mega-menu-photo-group[data-menu-link-label]');
  const LIST_COLUMN_COUNT = 1;

  groups.forEach((group) => {
    const label = group.getAttribute('data-menu-link-label')?.trim().toLowerCase();
    if (!label) return;

    let matchedItem = null;
    document.querySelectorAll('.menu-list__link-title').forEach((titleEl) => {
      if (matchedItem) return;
      if (titleEl.textContent?.trim().toLowerCase() === label) {
        matchedItem = titleEl.closest('.menu-list__list-item');
      }
    });

    if (!matchedItem) return;

    const target = matchedItem.querySelector('[data-menu-custom-cards]');
    if (!target) return;

    const cards = Array.from(group.children);
    if (!cards.length) return;

    const { desktop, tablet } = computeCardColumns(cards.length, LIST_COLUMN_COUNT);

    const list = matchedItem.querySelector('.mega-menu__list');
    if (list) {
      list.style.setProperty('--menu-columns-desktop', String(6 - desktop));
      list.style.setProperty('--menu-columns-tablet', String(4 - tablet));
    }

    const autoContent = matchedItem.querySelector('.mega-menu__content:not([data-menu-custom-cards])');
    if (autoContent) {
      autoContent.hidden = true;
    }

    const contentList = document.createElement('ul');
    contentList.className = 'mega-menu__content-list mega-menu__content-list--products list-unstyled';

    cards.forEach((card, index) => {
      const item = document.createElement('li');
      item.className = 'mega-menu__content-list-item';
      if (index + 1 > tablet) {
        item.classList.add('mega-menu__content-list-item--hidden-tablet');
      }
      item.appendChild(card);
      contentList.appendChild(item);
    });

    target.classList.add('mega-menu__content');
    target.style.setProperty('--menu-content-columns-desktop', String(desktop));
    target.style.setProperty('--menu-content-columns-tablet', String(tablet));
    target.replaceChildren(contentList);
    target.hidden = false;
  });
}

// The header menu's content gets re-hydrated client-side shortly after the
// initial page load (see the section-hydration import used elsewhere in this
// theme, and the data-hydration-key attributes on this menu's markup). That
// hydration swaps in a fresh, unprocessed copy of the DOM - so if our
// relocation already ran before hydration finished, its work gets discarded,
// which is why the photo cards only "sometimes" appeared. relocateMegaMenuPhotoCards
// is idempotent (a group with no children left has already been moved), so
// it's safe to simply re-run it on every relevant DOM mutation.
let relocateScheduled = false;
function scheduleMegaMenuPhotoCardRelocation() {
  if (relocateScheduled) return;
  relocateScheduled = true;
  requestAnimationFrame(() => {
    relocateScheduled = false;
    relocateMegaMenuPhotoCards();
  });
}

onDocumentLoaded(scheduleMegaMenuPhotoCardRelocation);

new MutationObserver(scheduleMegaMenuPhotoCardRelocation).observe(document.body, {
  childList: true,
  subtree: true,
});
